import { SupabaseClient } from '@supabase/supabase-js';

// This interface defines the structure of a game object, including the new persistence fields.
// It should be moved to a shared types file (e.g., 'src/types.ts') later for wider use.
export interface Game {
    id: string;
    // Add other existing game properties here, e.g., player1_id, player2_id
    status: 'active' | 'completed' | 'forfeited';
    game_state_json: any; // For type safety, this should be replaced with a specific interface for your game state.
    last_active_player: string;
    created_at: string;
    updated_at: string;
}

/**
 * Manages all database operations related to game state persistence.
 */
export class GameStateManager {
    private supabase: SupabaseClient;

    constructor(supabase: SupabaseClient) {
        this.supabase = supabase;
    }

    /**
     * Saves the current state of a game to the database.
     * This would be called after a player's turn or at regular intervals.
     * @param gameId The ID of the game to save.
     * @param gameState The full game state object to be stored in 'game_state_json'.
     * @param lastActivePlayerId The ID of the player who just made a move.
     */
    async saveGameState(gameId: string, gameState: any, lastActivePlayerId: string): Promise<{ error: Error | null }> {
        const { error } = await this.supabase
            .from('games')
            .update({
                game_state_json: gameState,
                last_active_player: lastActivePlayerId,
                // 'updated_at' is handled by the database trigger.
            })
            .eq('id', gameId);

        if (error) {
            console.error('Error saving game state:', error);
        }

        return { error };
    }

    /**
     * Loads the full state of a specific game from the database.
     * @param gameId The ID of the game to load.
     * @returns The loaded game object or null if not found.
     */
    async loadGameState(gameId: string): Promise<Game | null> {
        const { data, error } = await this.supabase
            .from('games')
            .select('*')
            .eq('id', gameId)
            .single();

        if (error) {
            console.error('Error loading game state:', error);
            return null;
        }

        return data as Game;
    }



    /**
     * Marks a game as 'completed'.
     * @param gameId The ID of the game to mark as completed.
     */
    async markGameCompleted(gameId: string): Promise<{ error: Error | null }> {
        const { error } = await this.supabase
            .from('games')
            .update({ status: 'completed' })
            .eq('id', gameId);

        if (error) {
            console.error('Error marking game as completed:', error);
        }
        return { error };
    }

    /**
     * Marks a game as 'forfeited'.
     * @param gameId The ID of the game to mark as forfeited.
     */
    async markGameForfeited(gameId: string): Promise<{ error: Error | null }> {
        const { error } = await this.supabase
            .from('games')
            .update({ status: 'forfeited' })
            .eq('id', gameId);

        if (error) {
            console.error('Error marking game as forfeited:', error);
        }
        return { error };
    }
}
